import { useContext } from 'react';

import { useInject } from '@vk-hr-tek/core/ioc';
import { AppError } from '@vk-hr-tek/core/error';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { FormError, TextInput, Form } from '@vk-hr-tek/app-ui/form';
import { FormKnownValue, FormKnownValues } from '@vk-hr-tek/ui/form';
import { Redo } from '@vk-hr-tek/ui/Redo';
import { Link } from '@vk-hr-tek/ui/Link';
import { Box } from '@vk-hr-tek/ui/Box';
import { Callout } from '@vk-hr-tek/ui/Callout';
import { Typography } from '@vk-hr-tek/ui/Typography';

import { useDispatch, useSelector } from '@app/hooks';

import { useAuth } from '../../../hooks';
import { LoginAliDto } from '../../../dto';
import {
  selectPhone,
  selectNotification,
  selectStatus,
  loginAli,
  sendAliPhoneCode,
  selectPassCode,
  changePhoneAliInit,
  redirectToQr,
  selectAuthInstance,
} from '../../../slice';
import { Button, Page, PageError } from '../../../segments';
import { AuthInstanceContext } from '../../AuthById';

export const Login = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useAuth();
  const validator = useInject(ValidationService);
  const authInstanceFromContext = useContext(AuthInstanceContext);

  const phone = useSelector(selectPhone);
  const error = useSelector(selectNotification);
  const status = useSelector(selectStatus);
  const passCode = useSelector(selectPassCode);
  const authInstance = useSelector(selectAuthInstance);

  const authInstanceId = authInstanceFromContext || authInstance.id;
  const checkWithoutPhoneCodeVerify =
    window.REACT_APP_VKHRTEK_AUTH_SKIP_PHONE_VERIFICATION.toString() === 'true';

  const { button_color: buttonColor, button_text: buttonText } = authInstance;

  if (!isAuthenticated && !passCode) {
    return (
      <PageError
        error={
          new AppError('client', {
            code: 400,
            message: 'Bad request',
            data: {
              error_code: 'invalid_ali_link',
            },
          })
        }
      />
    );
  }

  const onSubmit = (values: LoginAliDto) =>
    new Promise((resolve) => {
      dispatch(
        loginAli({
          values: {
            ...values,
            ...(authInstanceId && {
              authInstanceId,
            }),
          },
          actions: { resolve },
        }),
      );
    });

  const resendCode = () => {
    return new Promise((resolve) => {
      dispatch(
        sendAliPhoneCode({
          authInstanceId: authInstanceFromContext,
          actions: { resolve },
        }),
      );
    });
  };

  const changePhone = () => {
    return new Promise((resolve) => {
      dispatch(
        changePhoneAliInit({
          authInstanceId: authInstanceFromContext,
          actions: { resolve },
        }),
      );
    });
  };

  return (
    <Page title="Вход">
      <Form
        onSubmit={onSubmit}
        validate={(values) => validator.validate(values, LoginAliDto, ['form'])}
        render={({
          handleSubmit,
          submitting,
          pristine,
          hasValidationErrors,
          dirtySinceLastSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <FormKnownValues>
              <FormKnownValue label="Номер телефона" value={phone} />
            </FormKnownValues>
            <Box mt="24">
              <TextInput
                name="code"
                label="Код подтверждения"
                placeholder="Введите код"
                type="number"
                inputMode="numeric"
                autocomplete="one-time-code"
                id="qaAuthLoginFinishCodeInput"
                testId="test-id-verify-code-input"
              />
            </Box>
            <Box mb="12">
              <Callout>
                <Typography color="text.light.primary" variant="body3">
                  Код поступит в MAX или придёт по СМС
                </Typography>
              </Callout>
            </Box>
            <Redo label="Отправить код повторно" action={resendCode} />
            <Box mt={{ xs: '32', md: '48' }}>
              <Button
                color={buttonColor && `#${buttonColor}`}
                disabled={submitting || pristine || hasValidationErrors}
                testId="test-id-enter-button"
              >
                {buttonText || 'Войти в HR Tek'}
              </Button>
            </Box>
            {!dirtySinceLastSubmit && status && status === 'failed' && (
              <FormError error={error} />
            )}
            <Box
              mt={{ xs: '24', md: '16' }}
              display="flex"
              justifyContent={
                checkWithoutPhoneCodeVerify ? 'flex-end' : 'space-between'
              }
              alignItems="center"
            >
              {!checkWithoutPhoneCodeVerify && (
                <Link onClick={changePhone} size="small" variant="tertiary">
                  Изменился телефон
                </Link>
              )}
              <Link
                onClick={() => dispatch(redirectToQr())}
                size="small"
                variant="tertiary"
              >
                Выход к QR-коду
              </Link>
            </Box>
          </form>
        )}
      />
    </Page>
  );
};
